import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { getAccessToken as getPrivyAccessToken } from '@privy-io/react-auth';
import { clearSession, getAccessToken, getAuthSource } from '../auth/storage';
import { API_URL } from '../constants';
import { getInitialLanguage } from '../i18n/config';

export const instance = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json; charset=utf-8',
    },
});

function requestPath(config?: { baseURL?: string; url?: string }) {
    return `${config?.baseURL || ''}${config?.url || ''}`;
}

function currentLanguage() {
    return getInitialLanguage();
}

function isPublicAuthUrl(url?: string) {
    if (!url) return false;
    return (
        url.includes('user/login') ||
        url.includes('user/register') ||
        url.includes('user/forgot-password') ||
        url.includes('user/verify-otp')
    );
}

function redirectToLogin() {
    if (typeof window === 'undefined') return;
    const path = window.location.pathname;
    if (path === '/login' || path === '/register' || path === '/reset-password') return;
    const token = new URLSearchParams(window.location.search).get('token');
    if (path === '/red-packet' && token) return;
    window.location.replace('/login');
}

instance.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
        config.headers.set('x-lang', currentLanguage());

        if (isPublicAuthUrl(requestPath(config))) {
            return config;
        }

        const token =
            getAuthSource() === 'privy'
                ? (await getPrivyAccessToken()) || getAccessToken()
                : getAccessToken();

        if (token && !config.headers.Authorization) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        return config;
    },
    (error) => Promise.reject(error)
);

instance.interceptors.response.use(
    (response: AxiosResponse) => response,
    (error: AxiosError<{ message?: string }>) => {
        const publicAuth = isPublicAuthUrl(requestPath(error.config));

        if (error.response?.status === 401 && !publicAuth) {
            clearSession();
            redirectToLogin();
        }

        if (error.response) {
            return Promise.reject({
                status: error.response.status,
                message: error.response.data?.message,
            });
        }

        return Promise.reject(error);
    }
);
